<template>
  <section class="c2c-result">
    <div class="c2c-result-header">
      <span class="c2c-result-icon-wrap" aria-hidden="true">
        <Check :size="20" />
      </span>
      <h2>{{ t('result.heading') }}</h2>
    </div>

    <p class="c2c-result-filename" :title="result.filename">{{ result.filename }}</p>

    <p class="c2c-result-meta">
      <span>{{ formatBytes(result.totalBytes) }}</span>
      <span class="c2c-result-sep" aria-hidden="true">·</span>
      <span>{{ result.passthrough
        ? t('result.mode_passthrough')
        : t('result.encode_time_browser', { seconds: (result.encodeMs / 1000).toFixed(1) })
      }}</span>
      <span class="c2c-result-sep" aria-hidden="true">·</span>
      <span>{{ result.audioIncluded ? t('result.audio_included') : t('result.audio_none') }}</span>
    </p>

    <div class="c2c-result-actions">
      <CdxButton action="progressive" weight="primary" @click="openCommons">
        {{ t('result.view_commons') }}
        <ExternalLink :size="16" />
      </CdxButton>
      <CdxButton @click="$emit('new')">{{ t('result.upload_another') }}</CdxButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import { Check, ExternalLink } from "lucide-vue-next"
import { CdxButton } from "@wikimedia/codex"
import type { UploadResult } from "@/lib/types"

const { t } = useI18n()
const props = defineProps<{ result: UploadResult }>()
defineEmits<{ new: [] }>()

function openCommons() {
  window.open(props.result.pageUrl, "_blank", "noopener")
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B"
  if (bytes < 1024) return bytes + " B"
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + " MB"
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB"
}
</script>

<style scoped>
.c2c-result {
  padding: 20px 18px;
  background: #f3faf7;
  /* A full success-coloured border rings the whole card like a 2016 alert
     box. A hairline plus a coloured top edge says the same thing quietly. */
  border: 1px solid #c5e5da;
  border-top: 3px solid var(--border-color-success, #099979);
  border-radius: 8px;
  text-align: center;
}
.c2c-result-header {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 2px;
}
.c2c-result-icon-wrap {
  width: 24px; height: 24px; border-radius: 50%;
  background: var(--color-success, #177860);
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.c2c-result-icon-wrap :deep(svg) {
  width: 13px; height: 13px;
  color: var(--color-inverted-fixed, #fff);
  /* Centres the glyph optically; Codex's check sits a hair high in its box. */
  display: block;
}
.c2c-result h2 {
  margin: 0;
  color: var(--color-success, #177860);
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.c2c-result-filename {
  margin: 8px 0 6px 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  color: var(--color-base, #202122);
  word-break: break-all;
  line-height: 1.35;
}
.c2c-result-meta {
  margin: 0 0 14px 0;
  font-size: 12px;
  color: var(--color-subtle, #54595d);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  line-height: 1.4;
  /* Sizes and durations are figures; keep them from jittering. */
  font-variant-numeric: tabular-nums;
}
.c2c-result-sep { color: var(--border-color-base, #a2a9b1); }

.c2c-result-actions {
  display: flex; gap: 8px; justify-content: center;
  flex-wrap: wrap;
}
@media (max-width: 540px) {
  .c2c-result { padding: 14px 12px; }
  .c2c-result-actions { flex-direction: column; }
  .c2c-result-actions > * { width: 100%; }
}
</style>
